import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowDownWideNarrow, ArrowUpWideNarrow, ExternalLink, Loader2, Search, X } from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import { knowledgeService } from '../../services/knowledgeService';
import { ttqService, type TtqSubjectCode } from '../../services/ttqService';
import type { KnowledgeParticle } from '../../types/domain';
import { buildKnowledgeParticleDetailPath } from '../../utils/knowledgeNavigation';
import { getParticlePreviewText, getParticleTitle } from '../../utils/particleDisplay';
import { SUBJECT_CONFIG } from '../../utils/subjectConfig';
import { seedParticleSourceImageCache } from '../../features/ttqib/particleSourceImageCache';
import { setWorkbenchParticleCache } from '../../features/ttqib/ttqWorkbenchParticleCache';
import { TtqPickerParticleEmbeddedThumb } from './TtqPickerParticleEmbeddedThumb';

const PAGE_SIZE = 30;

type DateSortOrder = 'created_at_desc' | 'created_at_asc';

type AddParticleDraft = {
  selected: string[];
  search: string;
  onlyUnused: boolean;
  dateSort: DateSortOrder;
  scrollTop: number;
};

function draftStorageKey(sessionId: string): string {
  return `ttq-add-particle-draft:${sessionId}`;
}

export function peekTtqAddParticleDraft(sessionId: string): AddParticleDraft | null {
  try {
    const raw = sessionStorage.getItem(draftStorageKey(sessionId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<AddParticleDraft>;
    return {
      selected: Array.isArray(parsed.selected)
        ? parsed.selected.map((id) => String(id || '').trim()).filter(Boolean)
        : [],
      search: String(parsed.search || ''),
      onlyUnused: Boolean(parsed.onlyUnused),
      dateSort: parsed.dateSort === 'created_at_asc' ? 'created_at_asc' : 'created_at_desc',
      scrollTop: Math.max(0, Number(parsed.scrollTop) || 0),
    };
  } catch {
    return null;
  }
}

function writeTtqAddParticleDraft(sessionId: string, draft: AddParticleDraft): void {
  try {
    sessionStorage.setItem(draftStorageKey(sessionId), JSON.stringify(draft));
  } catch {
    // ignore quota / private mode
  }
}

export function clearTtqAddParticleDraft(sessionId: string): void {
  try {
    sessionStorage.removeItem(draftStorageKey(sessionId));
  } catch {
    // ignore
  }
}

type ParticleTtqMembership = {
  sessionCount: number;
  latestTitle: string | null;
};

function formatParticleUpdatedAt(value: string | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatStatusLabel(status: string | undefined): string | null {
  const raw = String(status || '').trim().toLowerCase();
  if (!raw) return null;
  if (raw === 'active' || raw === 'ready' || raw === 'published') return '可用';
  if (raw === 'draft') return '草稿';
  if (raw === 'archived') return '已归档';
  return status || null;
}

function formatMembershipBadge(membership: ParticleTtqMembership | undefined): {
  text: string;
  tone: 'idle' | 'used';
  title: string;
} {
  const count = membership?.sessionCount ?? 0;
  const latest = String(membership?.latestTitle || '').trim();
  if (count <= 0) {
    return {
      text: '品题 0',
      tone: 'idle',
      title: '尚未加入任何品题',
    };
  }
  const short = latest.length > 14 ? `${latest.slice(0, 14)}…` : latest;
  return {
    text: short ? `品题 ${count} · ${short}` : `品题 ${count}`,
    tone: 'used',
    title: latest
      ? `已加入 ${count} 个品题，最近：${latest}`
      : `已加入 ${count} 个品题`,
  };
}

type TtqAddParticleDialogProps = {
  open: boolean;
  sessionId: string;
  subject: TtqSubjectCode;
  excludedParticleIds: string[];
  onClose: () => void;
  onAdd: (particleIds: string[]) => Promise<void>;
};

export function TtqAddParticleDialog({
  open,
  sessionId,
  subject,
  excludedParticleIds,
  onClose,
  onAdd,
}: TtqAddParticleDialogProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [onlyUnused, setOnlyUnused] = useState(false);
  const [dateSort, setDateSort] = useState<DateSortOrder>('created_at_desc');
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [adding, setAdding] = useState(false);
  const [particles, setParticles] = useState<KnowledgeParticle[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [membershipById, setMembershipById] = useState<Record<string, ParticleTtqMembership>>({});
  const listRef = useRef<HTMLDivElement | null>(null);
  const [listRoot, setListRoot] = useState<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const loadingMoreRef = useRef(false);
  const requestIdRef = useRef(0);
  const membershipRequestIdRef = useRef(0);
  const pendingScrollTopRef = useRef<number | null>(null);
  const draftRestoredRef = useRef(false);

  const subjectLabel = SUBJECT_CONFIG[subject]?.label || subject;
  const dateSortDesc = dateSort === 'created_at_desc';

  useEffect(() => {
    if (!open) {
      draftRestoredRef.current = false;
      pendingScrollTopRef.current = null;
      setSearch('');
      setDebouncedSearch('');
      setOnlyUnused(false);
      setDateSort('created_at_desc');
      setSelected([]);
      return;
    }
    if (draftRestoredRef.current) return;
    draftRestoredRef.current = true;
    const draft = peekTtqAddParticleDraft(sessionId);
    if (!draft) return;
    setSearch(draft.search);
    setDebouncedSearch(draft.search.trim());
    setOnlyUnused(draft.onlyUnused);
    setDateSort(draft.dateSort);
    setSelected(draft.selected);
    pendingScrollTopRef.current = draft.scrollTop;
    clearTtqAddParticleDraft(sessionId);
  }, [open, sessionId]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), search.trim() ? 300 : 0);
    return () => clearTimeout(timer);
  }, [open, search]);

  useEffect(() => {
    if (!open || loading || particles.length === 0) return;
    const scrollTop = pendingScrollTopRef.current;
    if (scrollTop == null || !listRef.current) return;
    listRef.current.scrollTop = scrollTop;
    pendingScrollTopRef.current = null;
  }, [loading, open, particles.length]);

  const persistDraftAndOpenDetail = useCallback((particleId: string) => {
    const id = String(particleId || '').trim();
    if (!id || !sessionId) return;
    writeTtqAddParticleDraft(sessionId, {
      selected,
      search,
      onlyUnused,
      dateSort,
      scrollTop: listRef.current?.scrollTop ?? 0,
    });
    const returnTo = `/ttq/${encodeURIComponent(sessionId)}?addParticle=1`;
    navigate(buildKnowledgeParticleDetailPath(id, returnTo));
  }, [dateSort, navigate, onlyUnused, search, selected, sessionId]);

  const handleClose = useCallback(() => {
    clearTtqAddParticleDraft(sessionId);
    onClose();
  }, [onClose, sessionId]);

  const loadMemberships = useCallback(async (particleIds: string[]) => {
    const ids = Array.from(new Set(particleIds.map((id) => id.trim()).filter(Boolean)));
    if (!open || ids.length === 0) return;
    const requestId = ++membershipRequestIdRef.current;
    setMembershipById((current) => {
      const next = { ...current };
      for (const id of ids) {
        if (!next[id]) next[id] = { sessionCount: 0, latestTitle: null };
      }
      return next;
    });
    try {
      const rows = await ttqService.getParticleMemberships({
        particleIds: ids,
        subject,
      });
      if (requestId !== membershipRequestIdRef.current) return;
      setMembershipById((current) => {
        const next = { ...current };
        for (const id of ids) {
          next[id] = { sessionCount: 0, latestTitle: null };
        }
        for (const row of rows) {
          const particleId = String(row.particleId || '').trim();
          if (!particleId) continue;
          next[particleId] = {
            sessionCount: Number(row.sessionCount) || 0,
            latestTitle: row.latestTitle ? String(row.latestTitle) : null,
          };
        }
        return next;
      });
    } catch {
      // 标记失败时保留占位 0
    }
  }, [open, subject]);

  const loadPage = useCallback(async (nextPage: number, replace: boolean) => {
    if (!user?.id || !open) return;
    const requestId = ++requestIdRef.current;
    if (replace) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    try {
      const result = await knowledgeService.listKnowledgeParticles({
        userId: user.id,
        subject,
        page: nextPage,
        pageSize: PAGE_SIZE,
        search: debouncedSearch,
        sort: dateSort,
        ttqUnusedOnly: onlyUnused,
      });
      if (requestId !== requestIdRef.current) return;

      const excluded = new Set(excludedParticleIds);
      const batch = (result.data || []).filter((particle) => !excluded.has(particle.id));
      const pagination = result.pagination;
      const totalPages = Math.max(1, pagination?.totalPages ?? 1);
      const currentPage = pagination?.page ?? nextPage;

      for (const particle of batch) {
        setWorkbenchParticleCache(particle);
        seedParticleSourceImageCache(particle.id, particle.metadata);
      }

      setParticles((current) => {
        if (replace) return batch;
        const seen = new Set(current.map((item) => item.id));
        return [...current, ...batch.filter((item) => !seen.has(item.id))];
      });
      setPage(currentPage);
      setHasMore(currentPage < totalPages);
      void loadMemberships(batch.map((item) => item.id));
    } catch {
      if (requestId !== requestIdRef.current) return;
      if (replace) {
        setParticles([]);
        setHasMore(false);
        setPage(1);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [dateSort, debouncedSearch, excludedParticleIds, loadMemberships, onlyUnused, open, subject, user?.id]);

  useEffect(() => {
    if (!open) {
      setParticles([]);
      setMembershipById({});
      setPage(1);
      setHasMore(false);
      return;
    }
    void loadPage(1, true);
  }, [loadPage, open]);

  useEffect(() => {
    if (!open || !hasMore || loading || loadingMore) return;
    const root = listRef.current;
    const node = sentinelRef.current;
    if (!root || !node) return;

    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting) || loadingMoreRef.current || !hasMore) {
        return;
      }
      loadingMoreRef.current = true;
      void loadPage(page + 1, false).finally(() => {
        loadingMoreRef.current = false;
      });
    }, { root, rootMargin: '120px' });

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadPage, loading, loadingMore, open, page]);

  if (!open) return null;

  const toggle = (id: string) => {
    setSelected((current) => (
      current.includes(id) ? current.filter((x) => x !== id) : [...current, id]
    ));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-2xl border border-border bg-surface shadow-card">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h3 className="text-lg font-semibold text-ink">添加知识颗粒</h3>
          <button type="button" className="text-muted hover:text-ink" onClick={handleClose}>
            <X size={20} />
          </button>
        </div>
        <div className="space-y-2 border-b border-border px-5 py-3">
          <div className="flex items-center gap-2 rounded-lg border border-border px-3 py-2">
            <Search size={16} className="text-muted" />
            <input
              className="flex-1 bg-transparent text-sm outline-none"
              placeholder="搜索标题或题干"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border bg-canvas px-2.5 py-1.5 text-xs text-ink">
              <input
                type="checkbox"
                className="h-3.5 w-3.5 accent-primary"
                checked={onlyUnused}
                onChange={(event) => setOnlyUnused(event.target.checked)}
              />
              只看未品题颗粒
            </label>
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-md border border-border bg-canvas px-2.5 py-1.5 text-xs text-ink hover:bg-black/[0.03]"
              title={dateSortDesc ? '当前：日期新→旧，点击切换' : '当前：日期旧→新，点击切换'}
              onClick={() => {
                setDateSort((current) => (
                  current === 'created_at_desc' ? 'created_at_asc' : 'created_at_desc'
                ));
              }}
            >
              {dateSortDesc ? <ArrowDownWideNarrow size={14} /> : <ArrowUpWideNarrow size={14} />}
              {dateSortDesc ? '日期新→旧' : '日期旧→新'}
            </button>
          </div>
        </div>
        <div
          ref={(node) => {
            listRef.current = node;
            setListRoot(node);
          }}
          className="min-h-0 flex-1 overflow-y-auto px-5 py-3"
        >
          {loading && particles.length === 0 ? (
            <div className="flex justify-center py-8">
              <Loader2 className="animate-spin text-primary" />
            </div>
          ) : particles.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted">
              {onlyUnused ? '没有未品题的颗粒' : '未找到可添加的颗粒'}
            </p>
          ) : (
            <ul className="space-y-2">
              {particles.map((particle) => {
                const active = selected.includes(particle.id);
                const membershipBadge = formatMembershipBadge(membershipById[particle.id]);
                const title = getParticleTitle(particle, 96);
                const preview = getParticlePreviewText(particle, 100);
                const showPreview = preview && preview !== title;
                const statusLabel = formatStatusLabel(particle.status);
                const updatedAt = formatParticleUpdatedAt(particle.updated_at || particle.created_at);
                const particleSubject = String(particle.subject || '').trim() || subjectLabel;
                const tags = Array.isArray(particle.tags)
                  ? particle.tags.map((tag) => String(tag || '').trim()).filter(Boolean).slice(0, 3)
                  : [];

                return (
                  <li key={particle.id}>
                    <div
                      className={`flex items-stretch overflow-hidden rounded-xl border ${
                        active ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/30'
                      }`}
                    >
                      <button
                        type="button"
                        className="min-w-0 flex-1 px-4 py-3 text-left text-sm"
                        onClick={() => toggle(particle.id)}
                      >
                        <span className="line-clamp-2 font-medium text-ink">{title}</span>
                        {showPreview ? (
                          <span className="mt-1 line-clamp-2 block text-xs leading-relaxed text-muted">
                            {preview}
                          </span>
                        ) : null}
                        <TtqPickerParticleEmbeddedThumb
                          particleId={particle.id}
                          metadata={particle.metadata}
                          root={listRoot}
                        />
                        <span className="mt-2 flex flex-wrap items-center gap-1.5">
                          <span
                            className={
                              'inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-semibold '
                              + (membershipBadge.tone === 'used'
                                ? 'border-amber-200/80 bg-amber-50 text-amber-900'
                                : 'border-stone-200 bg-stone-50 text-stone-600')
                            }
                            title={membershipBadge.title}
                          >
                            {membershipBadge.text}
                          </span>
                          <span className="text-[10px] text-muted">{particleSubject}</span>
                          {statusLabel ? (
                            <span className="text-[10px] text-muted">· {statusLabel}</span>
                          ) : null}
                          {updatedAt ? (
                            <span className="text-[10px] text-muted">· {updatedAt}</span>
                          ) : null}
                          <span className="font-mono text-[10px] text-stone-400">
                            · {particle.id.slice(0, 8)}…
                          </span>
                        </span>
                        {tags.length > 0 ? (
                          <span className="mt-1.5 flex flex-wrap gap-1">
                            {tags.map((tag) => (
                              <span
                                key={tag}
                                className="rounded border border-border bg-canvas px-1.5 py-0.5 text-[10px] text-muted"
                              >
                                {tag}
                              </span>
                            ))}
                          </span>
                        ) : null}
                      </button>
                      <button
                        type="button"
                        className="flex shrink-0 flex-col items-center justify-center gap-1 border-l border-border/80 px-3 text-[10px] font-medium text-primary hover:bg-primary/10"
                        title="查看知识颗粒详情"
                        aria-label="查看知识颗粒详情"
                        onClick={(event) => {
                          event.stopPropagation();
                          persistDraftAndOpenDetail(particle.id);
                        }}
                      >
                        <ExternalLink size={16} strokeWidth={2} />
                        详情
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          {hasMore ? (
            <div ref={sentinelRef} className="flex justify-center py-3">
              {loadingMore ? <Loader2 className="h-4 w-4 animate-spin text-primary" /> : null}
            </div>
          ) : null}
        </div>
        <div className="border-t border-border px-5 py-4">
          <button
            type="button"
            className="w-full rounded-lg bg-primary py-2.5 text-sm font-medium text-white disabled:opacity-50"
            disabled={adding || selected.length === 0}
            onClick={() => {
              setAdding(true);
              void onAdd(selected)
                .then(() => {
                  clearTtqAddParticleDraft(sessionId);
                })
                .finally(() => setAdding(false));
            }}
          >
            {adding ? '添加中…' : `添加 ${selected.length} 个颗粒`}
          </button>
        </div>
      </div>
    </div>
  );
}
